import { useCallback, useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { useNavigation } from '../lib/useNavigation.jsx'
import { appRoutes } from '../lib/routes.jsx'
import ServiceWorkerUpdate from './ServiceWorkerUpdate'
import { useSupabaseAuth } from '../lib/useSupabaseAuth.jsx'
import { preloadCommonComponents } from '../lib/componentPreloader.js'
import { useSwipeGesture } from '../hooks/useSwipeGesture.js'
import { AppThemeProvider } from '../lib/AppThemeProvider'
import DemoBanner from './demo/DemoBanner'
import CompanyInfoGate from './onboarding/postlogin/CompanyInfoGate'

const ProtectedRoutes = () => {
  const { activeTab, navigateToTab } = useNavigation()
  const { user, isAuthenticated, loading } = useSupabaseAuth()

  // Fallback: If user is not authenticated but somehow reached protected routes
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      console.warn('Unauthenticated user reached protected routes, redirecting to home')
      // Clear any cached data and redirect
      try {
        localStorage.removeItem('supabase.auth.token')
        sessionStorage.clear()
      } catch (error) {
        console.warn('Could not clear auth cache:', error)
      }
      window.location.href = '/?message=session_expired'
    }
  }, [loading, isAuthenticated])

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  // Desktop-only collapsed (rail) state for the sidebar. Persisted to localStorage
  // so the user's choice survives reloads. Mobile drawer behavior is unaffected
  // because collapsed-rail styling only applies at the md breakpoint and up.
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage.getItem('prosperia.sidebar.collapsed') === '1'
    } catch (_) {
      return false
    }
  })

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev)
  }, [])

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false)
  }, [])

  const handleToggleCollapsed = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev
      try {
        window.localStorage.setItem('prosperia.sidebar.collapsed', next ? '1' : '0')
      } catch (_) { /* intentionally empty */ }
      return next
    })
  }, [])

  // Swipe gesture handler for mobile sidebar
  const handleSwipeRight = useCallback(() => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true)
    }
  }, [isSidebarOpen])

  // Initialize swipe gesture detection
  const swipeHandlers = useSwipeGesture(handleSwipeRight, {
    threshold: 50,
    maxVerticalDistance: 100,
    minHorizontalDistance: 30,
    enabled: true
  })

  // Lock body scroll when sidebar is open (mobile/PWA safety)
  useEffect(() => {
    try {
      if (isSidebarOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    } catch (_) { /* intentionally empty */ }
    return () => {
      try {
        document.body.style.overflow = ''
      } catch (_) { /* intentionally empty */ }
    }
  }, [isSidebarOpen])

  // Preload common components for faster navigation
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      // Preload common components in the background
      preloadCommonComponents().catch(error => {
        console.warn('Failed to preload common components:', error)
      })
    }
  }, [isAuthenticated, user?.id])

  // Handle tab changes
  const handleSetActive = useCallback((tab) => {
    navigateToTab(tab)
  }, [navigateToTab])

  return (
    <AppThemeProvider defaultTheme="dark">
      <CompanyInfoGate>
      <div className="min-h-screen overflow-x-hidden theme-bg-page theme-transition protected-routes-theme">
        {/* Demo Mode Banner */}
        <DemoBanner />
        {/* Service Worker Update Notification */}
        <ServiceWorkerUpdate />
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-20 backdrop-blur-xl ios:backdrop-blur-ios supports-[backdrop-filter]:backdrop-blur-xl bg-surface/80 border-b border-blue-500/30 p-3 flex items-center justify-between ios:pt-[env(safe-area-inset-top)] shadow-lg">
          <div></div>
          <button
            onClick={handleToggleSidebar}
            aria-label="Toggle navigation"
            className="touch-48 inline-flex items-center justify-center rounded-lg border border-blue-400/40 bg-blue-500/10 px-3 py-2 text-blue-100 hover:bg-blue-500/20 hover:text-white hover:border-blue-400/60 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200 [WebkitTapHighlightColor:transparent]"
          >
            <i className="fas fa-bars text-lg" />
          </button>
        </div>

        {/* Sidebar */}
        <Sidebar
          activeTab={activeTab}
          setActiveTab={handleSetActive}
          isOpen={isSidebarOpen}
          onClose={handleCloseSidebar}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapsed={handleToggleCollapsed}
        />

        {/* Click-to-close overlay on mobile */}
        {isSidebarOpen && (
          <button
            type="button"
            aria-label="Close sidebar"
            onClick={handleCloseSidebar}
            className="fixed inset-0 h-[100dvh] z-40 bg-black/50 md:hidden select-none [WebkitTapHighlightColor:transparent] backdrop-blur-md supports-[backdrop-filter]:backdrop-blur-md pointer-events-auto transition-all duration-300"
          />
        )}

        <div
          className={`transition-[margin-left] duration-300 swipe-enabled ${
            isSidebarCollapsed ? 'md:ml-16' : 'md:ml-72'
          } ${activeTab === 'ai-assistant' ? '' : 'px-4 md:px-6 py-4 md:py-8'}`}
          {...swipeHandlers}
        >
          <div className={`${activeTab === 'ai-assistant' ? 'w-full h-screen' : 'w-full max-w-[1600px] mx-auto'}`}>
            <Routes>
                {/* Redirect root to dashboard for authenticated users */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                {/* Render routes from configuration */}
                {appRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={route.element}
                  />
                ))}

                {/* 404 - Catch all unknown routes */}
                <Route path="*" element={
                <div className="min-h-screen flex items-center justify-center theme-bg-secondary">
                  <div className="text-center max-w-md mx-auto p-8">
                    <div className="w-20 h-20 rounded-2xl bg-red-500 flex items-center justify-center shadow-glow mb-6 mx-auto">
                      <i className="fas fa-exclamation-triangle text-white text-2xl"></i>
                    </div>
                    <h1 className="text-2xl font-bold theme-text-primary mb-4">Page Not Found</h1>
                    <p className="theme-text-secondary mb-6">The page you&apos;re looking for doesn&apos;t exist.</p>
                    <button
                      onClick={() => navigateToTab('dashboard')}
                      className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Go to Dashboard
                    </button>
                  </div>
                </div>
              } />
              </Routes>
          </div>
        </div>
      </div>
      </CompanyInfoGate>
    </AppThemeProvider>
  )
}

export default ProtectedRoutes